import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { GridRenderCellParams, GridTreeNodeWithRender } from "@mui/x-data-grid";
import {
  IBandwidthFailuresResponseModel,
  IStatisticsUptimeResponseModel,
} from "../../system-performance/models/chart";
import React, { useCallback, useMemo } from "react";

import { ApexOptions } from "apexcharts";
import Chart from "react-apexcharts";
import ChartIcon from "../../../../../common/components/icons/ChartIcon";
import { DEFAULT_STATISTICS_DATE_FORMAT } from "../common/values";
import { STATISTICS_BOX_HEIGHT } from "../../../../../common/variables/common";
import StyledDialog from "../../../../../common/components/dialog/StyledDialog";
import { SystemPerformanceResponseModel } from "../../system-performance/models/chart";
import { apexChartsTooltipStyles } from "../../../../../common/components/apexchart/StyledChart";
import { createChartTooltip } from "../common/chartTooltip";
import dayjs from "dayjs";
import defaultApexOptions from "../common/defaultApexOptions";
import { useDialog } from "../../../../../common/components/dialog/useDialog";

type ChartModalContainerProps = {
  data: SystemPerformanceResponseModel[];
  params: GridRenderCellParams<GridTreeNodeWithRender>;
  dialogTitle: string;
};

const ChartModalContainer: React.FC<ChartModalContainerProps> = ({
  data,
  params,
  dialogTitle,
}) => {
  const theme = useTheme();
  const { open, handleDialogOpen, handleDialogClose } = useDialog();

  const row = params.row as Record<string, any>;

  const tooltip = useMemo(() => createChartTooltip(), []);

  const defaultOptions = defaultApexOptions(theme);

  const findData = useMemo(() => {
    return data.filter(item => item.id === row.rowId);
  }, [data]);

  const isNodeUptime = (
    item: SystemPerformanceResponseModel
  ): item is IStatisticsUptimeResponseModel => "uptime_pct" in item;

  const isFailure = (
    item: SystemPerformanceResponseModel
  ): item is IBandwidthFailuresResponseModel => "total_failures" in item;

  const noDataToShow = useMemo(() => {
    if (findData.every(isNodeUptime)) {
      return findData.every(item => item.uptime_pct == null);
    }

    if (findData.every(isFailure)) {
      return findData.every(item => item.intervals_with_failures_pct == null);
    }

    return false;
  }, [findData]);

  const handleDialogOpenHandler = useCallback(() => {
    if (noDataToShow) {
      return;
    }

    handleDialogOpen();
  }, [noDataToShow, handleDialogOpen]);

  const { convertDates, convertValues } = useMemo(() => {
    const entries = Object.entries(row)
      .filter(([k]) => k.startsWith("uptimePct-"))
      .map(([k, v]) => {
        const date = dayjs(k.replace("uptimePct-", ""));

        const parsedValue = parseFloat(String(v).replace("%", ""));

        const value = v ? parsedValue : 0;

        return {
          date: date.toDate(),
          label: date.format(DEFAULT_STATISTICS_DATE_FORMAT),
          value,
        };
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return {
      convertDates: entries.map(e => e.label),
      convertValues: entries.map(e => e.value),
    };
  }, [row]);

  const options: ApexOptions = useMemo(
    () => ({
      tooltip,
      chart: {
        stacked: false,
        zoom: { enabled: false },
        toolbar: { show: false },
        fontFamily: "Inter",
      },
      theme: { mode: theme.palette.mode },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      markers:
        convertValues.length === 1 ? { size: 4, colors: ["#F3B344"] } : {},
      colors: ["#F3B344"],
      yaxis: {
        ...defaultOptions.yaxis,
        max: 100,
        labels: {
          formatter: function (value: number = 0) {
            return value.toString() + "%";
          },
        },
      },
      xaxis: { tooltip: { enabled: false }, categories: convertDates },
    }),
    [convertDates, convertValues, theme.palette.mode]
  );

  const series: ApexAxisChartSeries = useMemo(
    () => [{ name: row.itemName || "Uptime", data: convertValues }],
    [convertValues, row.itemName]
  );

  return (
    <>
      <Box
        onClick={handleDialogOpenHandler}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
          height: "100%",
          cursor: noDataToShow ? "not-allowed" : "pointer",
          transition: "color .2s",

          "&:hover .node-label": {
            color: noDataToShow
              ? theme.palette.otherTextTertiary.main
              : theme.palette.primary.main,
          },
          "&:hover .chart-icon": {
            fill: noDataToShow
              ? theme.palette.otherTextTertiary.main
              : theme.palette.primary.main,
          },
        }}
      >
        <ChartIcon />

        <Tooltip
          title={
            noDataToShow
              ? "No data to display " + `for ${row.itemName}`
              : row.itemName
          }
          arrow
          placement="top"
        >
          <Typography
            className="node-label"
            variant="body2Regular"
            fontWeight="bold"
          >
            {row.itemName}
          </Typography>
        </Tooltip>
      </Box>

      {open && (
        <StyledDialog
          open={open}
          onClose={handleDialogClose}
          maxWidth={false}
          showSubmitButton={false}
          title={dialogTitle}
          subTitle={`Chart for ${row.itemName}`}
          sx={{ width: "80%", m: "0 auto" }}
        >
          <Box sx={apexChartsTooltipStyles}>
            <Chart
              options={options}
              height={STATISTICS_BOX_HEIGHT}
              series={series}
            />
          </Box>
        </StyledDialog>
      )}
    </>
  );
};

export default React.memo(ChartModalContainer);
